// Room + doorway graph detection over a base's cell grid and wall/door state
// (base-walls.mjs). This is the bridge between geometry and combat: the
// combat resolver (base-defense-combat.mjs, via base-topology.mjs) consumes
// exactly the {rooms, doorways} shape this module produces — there is no
// separate graph-construction pass.
//
// Room identity is a WALL-bounded region: two cells merge into the same room
// only across an edge with neither a wall nor a door on it (an interior door
// still sits on a wall, so it never merges two rooms into one — it becomes a
// doorway *edge* between two distinct room nodes instead). Before any walls
// are drawn, every inside cell is one big unpartitioned room, matching the
// base's default state.

import { cellKey, edgeCells, exteriorBoundaryEdges, isWall, neighborEdges, parseCellKey } from "./base-walls.mjs";

export const EXTERIOR_ROOM_ID = "exterior";

function regionsOf(cells, wallState) {
  const seen = new Set();
  const regions = [];
  for (const start of cells) {
    if (seen.has(start)) continue;
    const region = new Set([start]);
    const stack = [start];
    seen.add(start);
    while (stack.length) {
      const [row, col] = parseCellKey(stack.pop());
      for (const [nr, nc, edgeKey] of neighborEdges(row, col)) {
        const neighborKey = cellKey(nr, nc);
        if (!cells.has(neighborKey) || seen.has(neighborKey) || isWall(wallState, edgeKey)) continue;
        seen.add(neighborKey);
        region.add(neighborKey);
        stack.push(neighborKey);
      }
    }
    regions.push(region);
  }
  return regions;
}

// A region fails the "room" shape iff every cell has an exposed side — i.e.
// it's a corridor when NO cell in it has all 4 orthogonal neighbors also in
// the region (catches straight and bent hallways alike; a real room needs at
// least one "fat" interior cell to pass).
function isCorridorShape(region) {
  for (const key of region) {
    const [row, col] = parseCellKey(key);
    const surrounded = neighborEdges(row, col).every(([nr, nc]) => region.has(cellKey(nr, nc)));
    if (surrounded) return false;
  }
  return true;
}

function boundaryEdgesOf(region) {
  const edges = new Set();
  for (const key of region) {
    const [row, col] = parseCellKey(key);
    for (const [nr, nc, edgeKey] of neighborEdges(row, col)) {
      if (!region.has(cellKey(nr, nc))) edges.add(edgeKey);
    }
  }
  return edges;
}

// detectRooms(cells, wallState) -> { rooms, doorways }
//   rooms:    [{ id, cellKeys: Set<string>, boundaryEdgeKeys: Set<string>, isCorridor }]
//   doorways: [{ id, edgeKey, roomIds: [aId, bId] }] -- bId is EXTERIOR_ROOM_ID
//             for a door on the base's outer boundary.
export function detectRooms(cells, wallState) {
  const regions = regionsOf(cells, wallState);
  const cellToRoomId = new Map();
  const rooms = regions.map((region, index) => {
    const id = `room:${index + 1}`;
    for (const key of region) cellToRoomId.set(key, id);
    return {
      id,
      cellKeys: region,
      boundaryEdgeKeys: boundaryEdgesOf(region),
      isCorridor: isCorridorShape(region),
    };
  });

  const doorways = (wallState?.doorEdgeKeys || []).map((edgeKey, index) => {
    const roomIds = edgeCells(edgeKey).map((side) => {
      const key = cellKey(...side);
      return cells.has(key) ? cellToRoomId.get(key) : EXTERIOR_ROOM_ID;
    });
    return { id: `doorway:${index + 1}`, edgeKey, roomIds };
  });

  return { rooms, doorways };
}

// exteriorTouchingRooms(detection, cells) -> Map<roomId, edgeKeys[]>
// Every room that shares at least one edge with the base's outer boundary,
// mapped to its (sorted) exterior edge keys. A room's boundaryEdgeKeys mixes
// exterior-facing and interior-doorway-facing edges together undifferentiated
// -- this is the missing rollup that separates them, by intersecting against
// exteriorBoundaryEdges(cells). A room absent from this map is fully interior.
export function exteriorTouchingRooms(detection, cells) {
  const exterior = exteriorBoundaryEdges(cells);
  const map = new Map();
  for (const room of detection.rooms) {
    const edges = [...room.boundaryEdgeKeys].filter((key) => exterior.has(key)).sort();
    if (edges.length) map.set(room.id, edges);
  }
  return map;
}

// Stable identity for a detected region: its lexicographically-smallest cell
// key. Independent of detection ordering (which `room.id` is not), so it's the
// right key for persisting per-region data — e.g. base-layout's safeRoomCellKey
// and the player's built room types (base.rooms). Accepts a room object or a
// raw cell-key iterable.
export function roomSignature(room) {
  const cellKeys = room?.cellKeys ?? room;
  let best = null;
  for (const key of cellKeys) if (best === null || key < best) best = key;
  return best;
}
